/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
} from 'react';
import { matchPath, useFetcher, useLocation } from 'react-router';
import LivePersonLauncher from '@/components/live-person/live-person-launcher';
import { createLogger } from '@/lib/logger';
import { LIVE_PERSON_ENGAGEMENT_ID, pushLivePersonCart, pushLivePersonCustomerInfo } from '@/lib/live-person/sdk';
import { resolveLivePersonVid } from '@/lib/live-person/attribution.client';
import { useBasket, useBasketSnapshot } from '@/providers/basket';
import { resourceRoutes, routes } from '@/route-paths';
import type { LivePersonConfig } from '@/types/config';

const logger = createLogger();

type ResourceRoutesWithLivePerson = typeof resourceRoutes & {
    livePersonAttribution?: string;
};

const livePersonAttributionRoute = (resourceRoutes as ResourceRoutesWithLivePerson).livePersonAttribution;

interface LivePersonContextValue {
    setCheckoutReviewActive(active: boolean): void;
}

const LivePersonContext = createContext<LivePersonContextValue | null>(null);

export interface LivePersonProviderProps extends PropsWithChildren {
    config?: LivePersonConfig;
    deliveryZip?: string;
    /** Ashley Next store/delivery skill when the location data source exposes it. */
    skillId?: string | null;
}

function findLivePersonEngagement(): HTMLElement | null {
    return document.querySelector<HTMLElement>(`#${LIVE_PERSON_ENGAGEMENT_ID} [data-lp-event]`);
}

function isLivePersonOverlayRendered(): boolean {
    return Boolean(document.querySelector('.LPMcontainer.LPMoverlay'));
}

export default function LivePersonProvider({ children, config, deliveryZip, skillId }: LivePersonProviderProps) {
    const location = useLocation();
    const attributionFetcher = useFetcher();
    const basket = useBasket();
    const basketId = useBasketSnapshot()?.basketId;
    const [engagementAvailable, setEngagementAvailable] = useState(false);
    const [checkoutReviewActive, setCheckoutReviewActive] = useState(false);
    const submittedAttributionRef = useRef<string | null>(null);
    const customerInfoSdeRef = useRef<string | null>(null);
    const cartSdeRef = useRef<string | null>(null);

    const enabled = config?.enabled === true;

    const captureAttribution = useCallback(async () => {
        if (!enabled || !window.lpTag) return;

        try {
            const lpvid = await resolveLivePersonVid(window.lpTag);
            if (!lpvid) return;

            // The action writes a first-party mirror even before a basket exists.
            // Include the current basket in the dedupe key so the same LPVID is
            // submitted once more when a basket first appears or is replaced.
            const attributionKey = `${lpvid}:${basketId ?? ''}`;
            if (submittedAttributionRef.current === attributionKey) return;

            if (!livePersonAttributionRoute) {
                logger.warn(
                    '[LivePerson] resourceRoutes.livePersonAttribution is unavailable; LPVID attribution was not submitted'
                );
                return;
            }

            await attributionFetcher.submit(
                { lpvid },
                {
                    method: 'POST',
                    action: livePersonAttributionRoute,
                    encType: 'application/json',
                }
            );
            submittedAttributionRef.current = attributionKey;
        } catch {
            logger.warn('[LivePerson] LPVID attribution submission failed; commerce remains available');
        }
    }, [attributionFetcher, basketId, enabled]);

    useEffect(() => {
        if (!enabled) {
            setEngagementAvailable(false);
            return;
        }

        const alreadyAvailable = isLivePersonOverlayRendered();
        setEngagementAvailable(alreadyAvailable);
        if (alreadyAvailable) {
            void captureAttribution();
            return;
        }

        const observer = new MutationObserver(() => {
            if (!isLivePersonOverlayRendered()) return;
            setEngagementAvailable(true);
            observer.disconnect();
            void captureAttribution();
        });
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
        };
    }, [captureAttribution, enabled]);

    useEffect(() => {
        const tag = window.lpTag;
        if (!enabled || !engagementAvailable || !tag || !config) return;

        if (deliveryZip) {
            const effectiveSkillId = skillId?.trim() || config.defaultSkillId;
            const customerInfoKey = `${effectiveSkillId}:${deliveryZip}`;
            if (
                customerInfoSdeRef.current !== customerInfoKey &&
                pushLivePersonCustomerInfo(tag, effectiveSkillId, deliveryZip)
            ) {
                customerInfoSdeRef.current = customerInfoKey;
            }
        }

        if (!basket || typeof basket.productTotal !== 'number') return;

        const productItems = basket.productItems ?? [];
        const products = productItems
            .map((item) => item.productName)
            .filter((productName): productName is string => typeof productName === 'string' && productName.length > 0);
        const cartSde = {
            total: basket.productTotal,
            numItems: productItems.length,
            products,
        };
        const cartKey = JSON.stringify(cartSde);
        if (cartSdeRef.current !== cartKey && pushLivePersonCart(tag, cartSde)) {
            cartSdeRef.current = cartKey;
        }
    }, [basket, config, deliveryZip, enabled, engagementAvailable, skillId]);

    const openChat = useCallback(() => {
        const engagement = findLivePersonEngagement();
        if (!engagement) {
            setEngagementAvailable(false);
            return;
        }
        engagement.click();
    }, []);

    const contextValue = useMemo<LivePersonContextValue>(
        () => ({ setCheckoutReviewActive }),
        [setCheckoutReviewActive]
    );
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const unprefixedPathSuffix = `/${pathSegments.slice(-2).join('/')}`;
    const isOrderConfirmation = Boolean(matchPath(routes.orderConfirmation, unprefixedPathSuffix));
    const launcherHidden = checkoutReviewActive || isOrderConfirmation;

    return (
        <LivePersonContext.Provider value={contextValue}>
            {children}
            {enabled && (
                <>
                    <div
                        id={LIVE_PERSON_ENGAGEMENT_ID}
                        aria-hidden="true"
                        className="fixed right-0 bottom-0 h-px w-px overflow-hidden opacity-0 pointer-events-none"
                    />
                    <LivePersonLauncher available={engagementAvailable} hidden={launcherHidden} onOpen={openChat} />
                </>
            )}
        </LivePersonContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLivePerson(): LivePersonContextValue {
    const context = useContext(LivePersonContext);
    if (!context) {
        throw new Error('useLivePerson must be used inside <LivePersonProvider>');
    }
    return context;
}
